import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AuthPage from './features/screens/auth/page';
import BudzdorovAuthError from './features/screens/auth/components/BudzdorovAuthError';
import DialogsPage from './features/screens/dialogs/page';
import StatisticsPageContainer from './features/screens/statistics/page';
import AdminPageContainer from './features/screens/admin/page';
import ProtocolsPage from './features/screens/protocols/page';
import CreateProtocolPage from './features/screens/protocols/create/page';
import EditProtocolPage from './features/screens/protocols/edit/page';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './store';
import { SnackbarProvider } from 'notistack';
import { setUser, clearUser, updateUserActivationStatus } from './userSlice';
import { clearAllChats } from './chatSlice';
import { getCurrentUser } from './features/utils/apiUtils';
import { SESSION_EXPIRED_EVENT } from './features/utils/sessionUtils';
import { hasProtocolsAccess, isUserAccountActive } from './features/utils/accessUtils';
import SubscriptionPage from './features/screens/subscription/SubscriptionPage';
import TariffSelectionPage from './features/screens/subscription/TariffSelectionPage';
import PaymentPage from './features/screens/subscription/PaymentPage';
import PaymentSuccessPage from './features/screens/subscription/PaymentSuccessPage';
import PaymentFailPage from './features/screens/subscription/PaymentFailPage';
import SessionExpiredDialog from './features/screens/dialogs/components/SessionExpiredDialog';
import { SidebarProvider, useSidebar } from './features/contexts/SidebarContext';
import { RenameChatProvider } from './features/contexts/RenameChatContext';
import { PictureInPictureProvider } from './features/contexts/PictureInPictureContext';
import Sidebar from './features/screens/dialogs/components/Sidebar';
import { usePeriodicActivationCheck } from './features/hooks/usePeriodicActivationCheck';
import ActivationGuard from './features/components/ActivationGuard';
import { useAuth } from './features/hooks/useAuth';
import { useGlobalChatStream } from './features/hooks/useGlobalChatStream';
import { useBudzdorovAuth } from './features/hooks/useBudzdorovAuth';
import SplashScreen from './features/components/SplashScreen';
import { useBrand } from './brands/contexts/BrandContext';
import { SubscriptionAccessProvider, useSubscriptionAccess } from './features/contexts/SubscriptionAccessContext';

// Компонент для защиты маршрутов протоколов
const ProtectedProtocolsRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !hasProtocolsAccess(user)) {
      // Если пользователь не из организации "Public", перенаправляем на диалоги
      navigate('/dialogs', { replace: true });
    }
  }, [user, navigate]);

  // Если нет доступа, не отображаем ничего (произойдет редирект)
  if (user && !hasProtocolsAccess(user)) {
    return null;
  }

  return <>{children}</>;
};

/** Подписка и оплата (Т-Банк) — только организация Public */
const ProtectedSubscriptionRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !hasProtocolsAccess(user)) {
      navigate('/dialogs', { replace: true });
    }
  }, [user, navigate]);

  if (user && !hasProtocolsAccess(user)) {
    return null;
  }

  return <>{children}</>;
};

const Redirector: React.FC = (): React.ReactElement => {
  const { user, isAuthenticated, loading } = useAuth();
  const subscriptionAccess = useSubscriptionAccess();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);

  // Обработка авторизации через BudZdorov API (только для бренда budzdorov)
  useBudzdorovAuth();

  // Инициализируем глобальный поток чатов
  useGlobalChatStream();

  // Сохраняем isDebug=true из query-параметров на любом экране в sessionStorage
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('isDebug') === 'true') {
      sessionStorage.setItem('isDebug', 'true');
    }
  }, [location.search]);

  // Синхронизируем состояние аутентификации с Redux
  useEffect(() => {
    if (user) {
      dispatch(setUser(user));
    } else {
      dispatch(clearUser());
      dispatch(clearAllChats());
    }
  }, [user, dispatch]);

  // Периодическая проверка сессии каждые 5 минут
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(async () => {
      try {
        const profile = await getCurrentUser();
        if (!profile) {
          setShowSessionExpiredDialog(true);
        } else {
          // Обновляем статус активации при проверке сессии
          dispatch(updateUserActivationStatus({ is_active: profile.is_active }));
        }
      } catch (error) {
        setShowSessionExpiredDialog(true);
      }
    }, 5 * 60 * 1000); // 5 минут

    return () => clearInterval(interval);
  }, [isAuthenticated, dispatch, navigate]);

  // Периодическая проверка статуса активации каждые 30 секунд
  usePeriodicActivationCheck({ intervalMs: 30000, enabled: isAuthenticated });

  // Обработчик события истекшего сеанса
  useEffect(() => {
    const handleSessionExpired = () => {
      setShowSessionExpiredDialog(true);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  useEffect((): void => {
    if (loading) return; // Ждем загрузки аутентификации

    // Public: ждём проверку подписки
    if (
      isAuthenticated &&
      user &&
      isUserAccountActive(user) &&
      user.organization === 'Public' &&
      subscriptionAccess.isLoading
    ) {
      return;
    }

    // Public без подписки — только маршруты покупки (сразу выбор тарифа, не пустой /subscription)
    if (isAuthenticated && user && isUserAccountActive(user) && subscriptionAccess.mustPurchaseSubscription) {
      const path = location.pathname;
      if (!path.startsWith('/subscription')) {
        navigate('/subscription/tariffs', { replace: true });
      } else if (path === '/subscription' || path === '/subscription/') {
        navigate('/subscription/tariffs', { replace: true });
      }
      return;
    }

    // Если мы во входе по BudZdorov (protocolId или userId на /admin),
    // не делаем автоматических редиректов — даём useBudzdorovAuth завершить авторизацию
    const searchParams = new URLSearchParams(location.search);
    const hasProtocolId = !!searchParams.get('protocolId');
    const hasAdminUserId =
      location.pathname === '/admin' && !!searchParams.get('userId');

    if (hasProtocolId || hasAdminUserId) {
      return;
    }

    if (isAuthenticated && user) {
      if (!isUserAccountActive(user)) {
        return;
      }

      // Проверяем доступ к протоколам для пользователей не из организации "Public"
      if ((location.pathname === '/protocols' || location.pathname.startsWith('/protocols/')) && !hasProtocolsAccess(user)) {
        navigate('/dialogs', { replace: true });
        return;
      }

      // Стандартный редирект на диалоги для неизвестных путей
      const allowed =
        location.pathname === '/dialogs' ||
        location.pathname === '/statistics' ||
        location.pathname === '/admin' ||
        location.pathname.startsWith('/protocols') ||
        location.pathname.startsWith('/subscription');
      if (!allowed) {
        navigate('/dialogs', { replace: true });
      }
    } else if (!isAuthenticated && location.pathname !== '/auth' && location.pathname !== '/auth-error' && !showSessionExpiredDialog) {
      navigate('/auth', { replace: true });
    }
  }, [
    isAuthenticated,
    user,
    loading,
    navigate,
    location.pathname,
    location.search,
    showSessionExpiredDialog,
    subscriptionAccess.isLoading,
    subscriptionAccess.mustPurchaseSubscription,
  ]);

  return (
    <>
      <SessionExpiredDialog
        isOpen={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
      />
    </>
  );
};

// Простой Splash экран на время инициализации аутентификации
const Splash: React.FC = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', width: '100vw', background: '#0f0f14', color: 'white'
  }}>
    <div style={{ fontSize: 18, opacity: 0.8 }}>Загрузка…</div>
  </div>
);

// Умный редирект с корня без мерцания: ждет загрузку auth
const RootRedirect: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();
  const { isLoading: subscriptionLoading, mustPurchaseSubscription } = useSubscriptionAccess();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    if (isAuthenticated && subscriptionLoading) return;
    // При BudZdorov-входе с protocolId не перехватываем редирект,
    // даем useBudzdorovAuth самостоятельно обработать авторизацию
    const searchParams = new URLSearchParams(window.location.search);
    const hasProtocolId = !!searchParams.get('protocolId');
    if (hasProtocolId) {
      return;
    }
    if (!isAuthenticated) {
      navigate('/auth', { replace: true });
      return;
    }
    navigate(mustPurchaseSubscription ? '/subscription/tariffs' : '/dialogs', { replace: true });
  }, [loading, isAuthenticated, subscriptionLoading, mustPurchaseSubscription, navigate]);
  return <Splash />;
};

// Компонент для отображения страниц с sidebar
const AppLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const user = useSelector((state: RootState) => state.user);
  const { isSidebarOpen, isMobile } = useSidebar();
  const location = useLocation();
  const { brandId } = useBrand();
  const { isLoading: subscriptionLoading, mustPurchaseSubscription } = useSubscriptionAccess();

  // Не авторизован или аккаунт не подтверждён (ActivationGuard обычно уже отдал экран ожидания)
  if (!user || !isUserAccountActive(user)) {
    return <>{children}</>;
  }

  // Public: пока проверяем подписку — без сайдбара
  if (user.organization === 'Public' && subscriptionLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center bg-primary-color"
        style={{ minHeight: '100vh', width: '100%' }}
      >
        <div style={{ color: '#64748b', fontSize: 16 }}>Загрузка…</div>
      </div>
    );
  }

  // Public без подписки — только контент покупки, без сайдбара
  if (mustPurchaseSubscription) {
    return (
      <div className="d-flex flex-row bg-primary-color app-layout app-layout--subscription-only" style={{ minHeight: '100vh', width: '100%' }}>
        <div className="main-content sidebar-hidden" style={{ width: '100%', maxWidth: '100%' }}>
          {children}
        </div>
      </div>
    );
  }

  // Для budzdorov показываем сайдбар только если был передан isDebug=true
  // (флаг сохраняется в sessionStorage через Redirector на любом экране).
  const shouldShowSidebar = brandId !== 'budzdorov' || sessionStorage.getItem('isDebug') === 'true';

  return (
    <div className="d-flex flex-row bg-primary-color app-layout">
      {shouldShowSidebar && <Sidebar />}
      <div
        className={`main-content ${shouldShowSidebar ? (isSidebarOpen ? 'sidebar-open' : 'sidebar-closed') : 'sidebar-hidden'}${isMobile && isSidebarOpen ? ' d-none' : ''}`}
      >
        {children}
      </div>
    </div>
  );
};

// Основной обертка приложения с проверкой инициализации
const AppRoutes: React.FC = () => {
  const { loading } = useAuth();

  // Пока идет инициализация - не показываем роуты (пустой экран)
  if (loading) {
    return null;
  }

  return (
    <>
      <Redirector />
      <Routes>
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth-error" element={<BudzdorovAuthError />} />
        <Route path="/dialogs" element={
          <ActivationGuard>
            <AppLayout><DialogsPage /></AppLayout>
          </ActivationGuard>
        } />
        <Route path="/statistics" element={
          <ActivationGuard>
            <AppLayout><StatisticsPageContainer /></AppLayout>
          </ActivationGuard>
        } />
        <Route path="/admin" element={
          <ActivationGuard>
            <AppLayout><AdminPageContainer /></AppLayout>
          </ActivationGuard>
        } />
        <Route path="/protocols" element={
          <ActivationGuard>
            <ProtectedProtocolsRoute>
              <AppLayout><ProtocolsPage /></AppLayout>
            </ProtectedProtocolsRoute>
          </ActivationGuard>
        } />
        <Route path="/protocols/create" element={
          <ActivationGuard>
            <ProtectedProtocolsRoute>
              <AppLayout><CreateProtocolPage /></AppLayout>
            </ProtectedProtocolsRoute>
          </ActivationGuard>
        } />
        <Route path="/protocols/:id/edit" element={
          <ActivationGuard>
            <ProtectedProtocolsRoute>
              <AppLayout><EditProtocolPage /></AppLayout>
            </ProtectedProtocolsRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><SubscriptionPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription/tariffs" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><TariffSelectionPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription/checkout" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><PaymentPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription/payment/success" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><PaymentSuccessPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription/payment/success/:orderId" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><PaymentSuccessPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/subscription/payment/fail" element={
          <ActivationGuard>
            <ProtectedSubscriptionRoute>
              <AppLayout><PaymentFailPage /></AppLayout>
            </ProtectedSubscriptionRoute>
          </ActivationGuard>
        } />
        <Route path="/" element={<RootRedirect />} />
      </Routes>
    </>
  );
};

// Компонент для управления splash screen (должен быть внутри BrowserRouter)
const SplashScreenWrapper: React.FC = () => {
  const location = useLocation();
  const [showSplash, setShowSplash] = useState(() => {
    // Не показываем splash screen на странице auth и auth-error
    if (location.pathname === '/auth' || location.pathname === '/auth-error') {
      return false;
    }

    // Используем флаг в window для отслеживания, был ли уже показан splash screen
    // При перезагрузке страницы window объект пересоздается, поэтому флаг сбрасывается
    const windowAny = window as any;

    if (!windowAny.__splashShown) {
      // Устанавливаем флаг, чтобы при навигации splash screen не показывался
      windowAny.__splashShown = true;
      return true;
    }

    // Если флаг установлен, значит это навигация через React Router
    return false;
  });

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // Не показываем splash screen на странице auth и auth-error
  if (location.pathname === '/auth' || location.pathname === '/auth-error') {
    return null;
  }

  return showSplash ? <SplashScreen onComplete={handleSplashComplete} /> : null;
};

const App: React.FC = (): React.ReactElement => {
  return (
    <SnackbarProvider
      maxSnack={3}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      autoHideDuration={3000}
      preventDuplicate={true}
    >
      <BrowserRouter>
        <SubscriptionAccessProvider>
          <SplashScreenWrapper />
          <SidebarProvider>
            <RenameChatProvider>
              <PictureInPictureProvider>
                <AppRoutes />
              </PictureInPictureProvider>
            </RenameChatProvider>
          </SidebarProvider>
        </SubscriptionAccessProvider>
      </BrowserRouter>
    </SnackbarProvider>
  );
};

export default App;
